import { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box, createStyles, makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import Slider, { Slide } from './Slide';
import Controls from './Controls';
import Spinner from 'components/UI/Spinner';

interface CarouselProps {
    slides: Slide[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            maxWidth: '900px',
            margin: '0 auto',
            overflow: 'hidden',
            [theme.breakpoints.down('sm')]: {
                maxWidth: '96%'
            }
        },
        slideContainer: {
            display: 'flex'
        },
        inlineNavigationButton: {
            'position': 'absolute',
            'top': '50%',
            'transform': 'translateY(-50%)',
            'width': '40px',
            'height': '40px',
            'borderRadius': '50%',
            'backgroundColor': '#fff',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'boxShadow': '0 2px 8px rgba(0,0,0,0.1)',
            'cursor': 'pointer',
            'zIndex': 10,
            'border': 'none',
            '&.prev': {
                left: theme.spacing(2)
            },
            '&.next': {
                right: theme.spacing(2)
            }
        },
        navigationButton: {
            position: 'absolute',
            top: '50%',
            right: theme.spacing(2),
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            zIndex: 10
        },
        navigationButtonPrev: {
            position: 'absolute',
            top: '50%',
            left: theme.spacing(2),
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            zIndex: 10
        },
        videoModalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: theme.spacing(2)
        },
        videoModalContent: {
            position: 'relative',
            width: '100%',
            maxWidth: '900px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            overflow: 'hidden'
        },
        videoModalClose: {
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10
        },
        videoIframe: {
            width: '100%',
            aspectRatio: '16/9',
            border: 'none'
        },
        closeIcon: {
            cursor: 'pointer',
            [theme.breakpoints.down('sm')]: {
                fontSize: '16'
            }
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            aspectRatio: '16/9',
            backgroundColor: '#f5f5f5',
            position: 'absolute'
        }
    })
);

export default function Carousel({ slides }: CarouselProps) {
    const classes = useStyles();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
    const [isVideoLoading, setIsVideoLoading] = useState(false);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;

        setSelectedIndex(emblaApi.selectedScrollSnap());
        setPrevBtnEnabled(emblaApi.canScrollPrev());
        setNextBtnEnabled(emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;

        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    // Function to open video modal
    const openVideoModal = (videoUrl: string) => {
        setCurrentVideoUrl(videoUrl);
        setVideoModalOpen(true);
        setIsVideoLoading(true);
    };

    // Function to close video modal
    const closeVideoModal = () => {
        setVideoModalOpen(false);
        setCurrentVideoUrl(null);
    };

    const handleIframeLoad = () => {
        setIsVideoLoading(false);
    };

    const getLoomEmbedUrl = (shareUrl: string) => {
        // Extract the video ID from the share URL
        const videoId = shareUrl.split('/').pop()?.split('?')[0];
        if (!videoId) return shareUrl;

        // Return the embed URL format
        return `https://www.loom.com/embed/${videoId}`;
    };

    return (
        <Box className={classes.root}>
            <div
                className="embla"
                ref={emblaRef}
            >
                <Box className={classes.slideContainer}>
                    {slides.map(slide => (
                        <Slider
                            key={slide.id}
                            slide={slide}
                            onVideoClick={() => slide.videoUrl && openVideoModal(slide.videoUrl)}
                        />
                    ))}
                </Box>
            </div>

            {!isMobile && slides.length > 1 && (
                <>
                    <button
                        className={`${classes.inlineNavigationButton} prev`}
                        onClick={scrollPrev}
                        disabled={!prevBtnEnabled}
                    >
                        <FontAwesomeIcon
                            icon={faChevronLeft}
                            size="lg"
                        />
                    </button>
                    <button
                        className={`${classes.inlineNavigationButton} next`}
                        onClick={scrollNext}
                        disabled={!nextBtnEnabled}
                    >
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            size="lg"
                        />
                    </button>
                </>
            )}

            <Controls
                selectedIndex={selectedIndex}
                slidesCount={slides.length}
                onPrevClick={scrollPrev}
                onNextClick={scrollNext}
                onDotClick={scrollTo}
                isMobile={isMobile}
            />

            {videoModalOpen && currentVideoUrl && (
                <Box
                    className={classes.videoModalOverlay}
                    onClick={closeVideoModal}
                >
                    <Box
                        className={classes.videoModalContent}
                        onClick={e => e.stopPropagation()}
                    >
                        <Box
                            className={classes.videoModalClose}
                            onClick={closeVideoModal}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className={classes.closeIcon}
                                onClick={closeVideoModal}
                            />
                        </Box>

                        {isVideoLoading && (
                            <Box className={classes.loadingContainer}>
                                <Spinner />
                            </Box>
                        )}

                        <iframe
                            className={classes.videoIframe}
                            src={getLoomEmbedUrl(currentVideoUrl)}
                            onLoad={handleIframeLoad}
                            allowFullScreen
                        ></iframe>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
